import {Stack, Text} from '@mantine/core';
import React, {useEffect, useMemo} from 'react';

import {Section} from 'constants/NavConstants';
import {OligoAwareFolderItemType} from 'constants/RegistryConstants';
import BioentitySearchView, {clearBioentitySearchViewSearchParams} from 'molbio/BioentitySearchView';
import {ItemListingSchemasByType, SchemasFilterFunction} from 'nav/sidepanel/listing/ItemListingUtil';
import {ProteinNestedComponentStructuredTableColumn} from 'notebook/structured-tables/StructuredTableConstants';
import {Filter, FilterKey} from 'search/SearchConstants';
import {RowSelectionMode} from 'ui-library/scrollable/ScrollableTableConstants';

const NAV_SECTION = Section.ANTIBODY_NESTED_DATA_CREATION;

/**
 * Limits the type and schema picker to the schemas the column accepts. Locking the column's schema filter isn't
 * enough on its own, because choosing a type or schema in the picker replaces the schema filter.
 */
function getAcceptedSchemasFilter(
  column: ProteinNestedComponentStructuredTableColumn
): SchemasFilterFunction | undefined {
  const schemaFilter = column.additionalFilters?.find((filter) => filter.key === FilterKey.SCHEMA);
  if (schemaFilter == null || !Array.isArray(schemaFilter.value)) {
    return undefined;
  }
  const acceptedSchemaIds = new Set<unknown>(schemaFilter.value);
  return (schemasByAvailableType: ItemListingSchemasByType): ItemListingSchemasByType =>
    Object.fromEntries(
      Object.entries(schemasByAvailableType).map(([type, schemas]) => [
        type,
        schemas.filter((schema) => acceptedSchemaIds.has(schema.id)),
      ])
    );
}

interface ProteinCreationSearchPaneProps {
  column: ProteinNestedComponentStructuredTableColumn;
  selectedIds: Array<string>;
  onSelectIds: (ids: Array<string>) => void;
}

/**
 * Searches for registered entities that the component column accepts. Its schema filters can't be removed.
 */
export default function ProteinCreationSearchPane({
  column,
  selectedIds,
  onSelectIds,
}: ProteinCreationSearchPaneProps): JSX.Element {
  const availableTypes = useMemo(
    () => column.bioentityTypeFilters as Array<OligoAwareFolderItemType>,
    [column]
  );
  const initialFilters = useMemo(
    (): Array<Filter> => (column.additionalFilters ?? []).map((filter) => ({...filter, locked: true})),
    [column]
  );
  const filterSchemas = useMemo(() => getAcceptedSchemasFilter(column), [column]);

  // The search view otherwise reopens on the last search in this nav section, which may be for another column.
  useEffect(() => {
    clearBioentitySearchViewSearchParams(availableTypes, NAV_SECTION, initialFilters);
  }, [availableTypes, initialFilters]);

  return (
    // Fills the height the modal body leaves it, so that only the results scroll.
    <Stack spacing="xs" mih={0} sx={{flex: 1}}>
      <Text size="sm">Search for registered sequences to add to {column.name}.</Text>
      <Stack
        spacing={0}
        mih={0}
        // The listing already insets its header, so cancel the modal body's padding (the theme's lg spacing).
        sx={(theme) => ({flex: 1, marginInline: `calc(-1 * ${theme.spacing.lg})`})}
      >
        <BioentitySearchView
          availableTypes={availableTypes}
          initialFilters={initialFilters}
          filterSchemas={filterSchemas}
          navSection={NAV_SECTION}
          rowSelectionMode={RowSelectionMode.MULTIPLE}
          selectedBioentityIds={selectedIds}
          setSelectedBioentityIds={onSelectIds}
        />
      </Stack>
    </Stack>
  );
}
